import HttpService from "./HttpService";

/**
 * Service for handling paycard upgrade operations
 * This service handles the business logic for upgrading paycards by:
 * 1. Finding the customer associated with the assigned card's email
 * 2. Creating a bulk action of type UPGRADE_PAYCARD
 */
class PaycardUpgradeService {
  /**
   * Upgrades a paycard by creating a bulk action
   * @param {Object} upgradeData - The upgrade request data
   * @param {string} upgradeData.currentCardId - ID of the current assigned card
   * @param {string} upgradeData.newCardId - ID of the new card to upgrade to
   * @param {string} upgradeData.employeeNo - Employee number
   * @returns {Promise} Response from the upgrade operation
   */
  static async upgradeCard(upgradeData) {
    try {
      // Step 1: Get the assigned card details to retrieve the email
      const assignedCardResponse = await HttpService.getAxiosClient().get(
        `${window.API_URL}/paycards/assigned/${upgradeData.currentCardId}`
      );
      
      const assignedCard = assignedCardResponse.data;
      const customerEmail = assignedCard.email;
      
      if (!customerEmail || customerEmail.startsWith("paycard_noemail_")) {
        throw new Error("Cannot upgrade card: No valid email address found for the assigned card");
      }

      // Step 2: Find the customer by email address
      const customerResponse = await HttpService.getAxiosClient().get(
        `${window.API_URL}/customers/search?email=${encodeURIComponent(customerEmail)}`
      );
      
      if (!customerResponse.data || customerResponse.data.length === 0) {
        throw new Error(`Cannot upgrade card: No customer found with email ${customerEmail}`);
      }
      
      const customer = customerResponse.data[0]; // Take the first match
      const customerId = customer.id || customer.customerId || customer.idCustomer;
      
      if (!customerId) {
        throw new Error("Cannot upgrade card: Customer ID not found");
      }

      // Step 3: Create the bulk action for UPGRADE_PAYCARD
      const bulkActionPayload = {
        actionType: "UPGRADE_PAYCARD",
        customerId: customerId,
        cardId: upgradeData.newCardId,
        currentCardId: upgradeData.currentCardId,
        employeeNo: upgradeData.employeeNo,
        customerEmail: customerEmail,
        metadata: {
          upgradeTimestamp: new Date().toISOString(),
          initiatedBy: "admin-web",
          reason: "Card upgrade request"
        }
      };

      // Step 4: Submit the bulk action
      const bulkActionResponse = await HttpService.getAxiosClient().post(
        `${window.API_URL}/bulk-actions`,
        bulkActionPayload
      );

      return {
        success: true,
        bulkActionId: bulkActionResponse.data.id,
        customerId: customerId,
        message: "Card upgrade bulk action created successfully",
        data: bulkActionResponse.data
      };

    } catch (error) {
      // Enhanced error handling
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        const serverError = error.response.data?.message || error.response.data?.error || error.message;
        throw new Error(`Upgrade failed: ${serverError}`);
      } else if (error.request) {
        // The request was made but no response was received
        throw new Error("Upgrade failed: No response from server");
      } else {
        // Something happened in setting up the request that triggered an Error
        throw new Error(`Upgrade failed: ${error.message}`);
      }
    }
  }

  /**
   * Alternative method that directly calls the upgrade endpoint
   * This is what the current UpgradeCardDialog expects
   * @param {Object} upgradeData - The upgrade request data
   * @returns {Promise} Response from the upgrade operation
   */
  static async upgradeCardDirect(upgradeData) {
    try {
      const response = await HttpService.getAxiosClient().post(
        `${window.API_URL}/paycards/upgrade`,
        upgradeData
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get upgrade history for a specific card
   * @param {string} cardId - The card ID to get upgrade history for
   * @returns {Promise} Array of upgrade records
   */
  static async getUpgradeHistory(cardId) {
    try {
      const response = await HttpService.getAxiosClient().get(
        `${window.API_URL}/paycards/${cardId}/upgrade-history`
      );
      return response.data;
    } catch (error) {
      throw new Error(`Failed to get upgrade history: ${error.message}`);
    }
  }

  /**
   * Check if a card is eligible for upgrade
   * @param {string} cardId - The card ID to check
   * @returns {Promise} Eligibility information
   */
  static async checkUpgradeEligibility(cardId) {
    try {
      const response = await HttpService.getAxiosClient().get(
        `${window.API_URL}/paycards/${cardId}/upgrade-eligibility`
      );
      return response.data;
    } catch (error) {
      throw new Error(`Failed to check upgrade eligibility: ${error.message}`);
    }
  }
}

export default PaycardUpgradeService;
